import { PkButton, PkChip, PkIcon } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo, useEffect, useRef, useState } from 'react';
import type Swiper from 'swiper';
import { Controller, Navigation } from 'swiper/modules';
import { Swiper as SwiperCarousel, useSwiper, SwiperSlide } from 'swiper/react';

import { useLivescoresTabs } from '../../hooks/tabs';
import { scrollToTab } from '../../utils/scroll';
import { track } from '../../utils/tracking';
import CalendarTab from '../CalendarTab';

import { useWindowSize } from '@hooks/useWindowSize';
import { withErrorBoundary } from '@shared/ErrorBoundary';
import { useTranslateFn } from '@shared/Translate';

import * as styles from './styles.module.scss';
import * as tabStyles from '../CalendarTab/styles.module.scss';

const CarouselHandler = () => {
  const { selectedTabIndex } = useLivescoresTabs();
  const controlledSwiper = useSwiper();

  useEffect(() => {
    if (!controlledSwiper || selectedTabIndex < 0) return;
    controlledSwiper.slideTo(selectedTabIndex, 300);
  }, [controlledSwiper, selectedTabIndex]);

  return null;
};

const CalendarTabs = () => {
  const t = useTranslateFn();
  const swiperRef = useRef(null);

  const [prevEl, setPrevEl] = useState<HTMLDivElement>(null);
  const [nextEl, setNextEl] = useState<HTMLDivElement>(null);
  const { isMobile } = useWindowSize();
  const { selectedTabIndex, tabs, isFetchingPreviousPage, hasPreviousPage, loadPrevious, loadNext, selectedTab } =
    useLivescoresTabs();

  const handleNextClick = () => {
    track(`Click date-arrow future`, 'uefa_calendar_date_arrow');
  };
  const handlePrevClick = () => {
    track(`Click date-arrow past`, 'uefa_calendar_date_arrow');
  };
  const handleClick = () => {
    loadPrevious().then(({ data }) => {
      const firstTab = data?.at(0);
      firstTab && scrollToTab(firstTab.id);
    });
  };

  const handleSwiperEdge = ({ isEnd }: Swiper) => {
    isEnd && loadNext();
  };

  const handleFocus = () => {
    const isSwiperFocused = document.activeElement === swiperRef?.current;

    if (isSwiperFocused) {
      document.getElementById(`calendar-tab-${selectedTab?.id}`)?.focus();
    }
  };

  if (!tabs?.length || selectedTabIndex < 0) return null;

  return (
    <div className={styles.wrapper} onFocus={handleFocus}>
      <div ref={setPrevEl} className={styles.buttonPrev}>
        <PkButton appearance='icon' soft onClick={handlePrevClick}>
          <PkIcon name='navigation-chevron-left' size={24} />
        </PkButton>
      </div>
      <SwiperCarousel
        ref={swiperRef}
        tabIndex={0}
        initialSlide={selectedTabIndex}
        modules={[Controller, Navigation]}
        slidesPerView='auto'
        onToEdge={handleSwiperEdge}
        slidesPerGroupAuto
        centeredSlides
        centeredSlidesBounds
        resizeObserver
        observer
        observeSlideChildren
        spaceBetween={isMobile ? 16 : 12}
        freeMode={{
          sticky: true,
        }}
        className={styles.slider}
        navigation={{ nextEl, prevEl }}
      >
        <CarouselHandler />
        <SwiperSlide className={styles.slide}>
          <PkChip
            className={clsx(tabStyles.tab, (isFetchingPreviousPage || !hasPreviousPage) && tabStyles.disabled)}
            onPkClick={handleClick}
          >
            <span className='pk-font--base-regular pk-font-size--s pk-text--nowrap'>{t('load_prev_matches')}</span>
          </PkChip>
        </SwiperSlide>

        {tabs.map((tab, index) => (
          <SwiperSlide key={tab.id} className={styles.slide}>
            <CalendarTab tab={tab} tabIndex={index} />
          </SwiperSlide>
        ))}
      </SwiperCarousel>
      <div ref={setNextEl} className={styles.buttonNext}>
        <PkButton appearance='icon' soft onClick={handleNextClick}>
          <PkIcon name='navigation-chevron-right' size={24} />
        </PkButton>
      </div>
    </div>
  );
};

export { CalendarTabs as PureCalendarTabs };

export default withErrorBoundary(memo(CalendarTabs));
